import React, { useState, useEffect, useContext, useRef } from "react";
import Header from "../components/HeaderB";
import ProfileSidebar from "../components/ProfileSideBar";
import { AuthContext } from "../context/AuthContext";
import { Tag, Pencil, Trash2, ArrowUpDown, ChevronRight, Plus } from "lucide-react";
import { translateText } from "../utils/categoryTranslations";

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshFlag, setRefreshFlag] = useState(false);
  const [expandedRow, setExpandedRow] = useState(null);

  const [sortField, setSortField] = useState("category_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortVisible, setSortVisible] = useState(false);

  const [deletePopup, setDeletePopup] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  const [editPopup, setEditPopup] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);

  // Add Category States
  const [showCategoryPopup, setShowCategoryPopup] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");

  const { user, accessToken, language } = useContext(AuthContext);

  const translations = {
    el: {
      title: "Διαχείριση κατηγοριών",
      sort: "Ταξινόμηση",
      sortBy: "Ταξινόμηση κατά",
      nameEn: "Όνομα (EN)",
      nameEl: "Όνομα (ΕΛ)",
      actions: "Ενέργειες",
      noCategories: "Δεν βρέθηκαν κατηγορίες.",
      delTitle: "Διαγραφή κατηγορίας",
      delMsg: "Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την κατηγορία;",
      cancel: "Ακύρωση",
      delete: "Διαγραφή",
      edit: "Επεξεργασία",
      editTitle: "Επεξεργασία κατηγορίας",
      save: "Αποθήκευση",
      asc: "Αύξουσα",
      desc: "Φθίνουσα",
      az: "Α-Ω",
      za: "Ω-Α",
      loading: "Φόρτωση...",
      addCat: "Προσθήκη κατηγορίας",
    },
    en: {
      title: "Manage categories",
      sort: "Sort",
      sortBy: "Sort by",
      nameEn: "Name (EN)",
      nameEl: "Name (EL)",
      actions: "Actions",
      noCategories: "No categories found.",
      delTitle: "Delete category",
      delMsg: "Are you sure you want to delete this category?",
      cancel: "Cancel",
      delete: "Delete",
      edit: "Edit",
      editTitle: "Edit category",
      save: "Save",
      asc: "Ascending",
      desc: "Descending",
      az: "A-Z",
      za: "Z-A",
      loading: "Loading...",
      addCat: "Add category",
    }
  };

  const t = translations[language] || translations.el;

  const isAlphabeticalSortField = (field) => ["category_name", "category_name_el"].includes(field);
  const getOrderLabel = (value) => {
    if (isAlphabeticalSortField(sortField)) {
      return value === "asc" ? t.az : t.za;
    }
    return value === "asc" ? t.asc : t.desc;
  };

  const initialLoad = useRef(true);

  useEffect(() => {
    const fetchCategories = async () => {
      if (!user || !accessToken) return;
      try {
        const params = new URLSearchParams({ sortField, sortOrder });
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/admincategories/allcategories?${params}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        const data = await res.json();
        setCategories(data.categories || []);
      } catch (err) {
        console.error(err);
      } finally {
        initialLoad.current = false;
      }
    };
    fetchCategories();
  }, [user, accessToken, sortField, sortOrder, refreshFlag]);

  const toggleExpand = (id) => setExpandedRow(expandedRow === id ? null : id);

  const handleEditOpen = (cat) => {
    setEditForm({ category_id: cat.category_id, category_name: cat.category_name, category_name_el: cat.category_name_el });
    setEditPopup(true);
  };

  const handleEditSave = async () => {
    try {
      setSaving(true);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/admincategories/${editForm.category_id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify({ category_name: editForm.category_name, category_name_el: editForm.category_name_el }),
        }
      );
      if (!res.ok) throw new Error("Failed to update");
      setRefreshFlag(prev => !prev);
      setEditPopup(false);
      setEditForm(null);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/admincategories/${categoryToDelete}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (!res.ok) throw new Error("Failed to delete");
      setRefreshFlag(prev => !prev);
      setDeletePopup(false);
      setCategoryToDelete(null);
    } catch (err) {
      console.error(err);
    }
  };

  // Handler for adding a new category from the panel
  const handleSaveCategory = async () => {
    if (!newCategoryName) return;

    const fromlanguage = language;
    const tolanguage = language === "en" ? "el" : "en";

    try {
      const translated = await translateText(newCategoryName, fromlanguage, tolanguage);

      const payload = {
        nameEn: language === "en" ? newCategoryName : translated,
        nameEl: language === "el" ? newCategoryName : translated
      };

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/adddiscount/categories/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setRefreshFlag(prev => !prev);
        setShowCategoryPopup(false);
        setNewCategoryName("");
      }
    } catch (err) {
      console.error("Error adding category:", err);
    }
  };

  return (
    <div className="min-h-screen bg-custom-background flex flex-col w-full overflow-x-hidden">
      <Header />
      <div className="flex-1 flex flex-row relative bg-custom-background w-full">
        <ProfileSidebar />
        <main id="admincategories-content" className="flex flex-col flex-1 items-center justify-start px-3 sm:px-6 md:px-10 py-10 xl:ml-20 text-center md:text-left w-full min-w-0">

          {/* Title + controls bar (Forced horizontally on all viewports) */}
          <div className="flex flex-row justify-between items-center mb-8 gap-2 w-full max-w-3xl mt-12 md:mt-0">
            <h2 className="text-sm xs:text-base sm:text-lg md:text-2xl font-semibold text-custom-letters flex items-center gap-1.5 min-w-0 text-left">
              <Tag className="text-custom-grad1 flex-shrink-0 w-4 h-4 xs:w-5 xs:h-5 md:w-6 md:h-6" /> 
              <span className="truncate">{t.title}</span>
            </h2>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <button onClick={() => setShowCategoryPopup(true)} title={t.addCat}
                className="bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white p-2 xs:p-2.5 sm:p-3 rounded-full hover:opacity-90 transition shadow-sm">
                <Plus className="w-4 h-4 xs:w-[18px] xs:h-[18px]" />
              </button>
              <button onClick={() => setSortVisible(!sortVisible)} title={t.sort}
                className="bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white p-2 xs:p-2.5 sm:p-3 rounded-full shadow-sm">
                <ArrowUpDown className="w-4 h-4 xs:w-[18px] xs:h-[18px]" />
              </button>
            </div>
          </div>

          {/* Sort panel */}
          {sortVisible && (
            <div className="bg-custom-header p-4 rounded-xl shadow mb-6 w-full max-w-xs mx-auto sm:mx-0">
              <p className="text-sm font-semibold mb-2 text-custom-letters text-left">{t.sortBy}</p>
              <select value={sortField} onChange={(e) => setSortField(e.target.value)}
                className="w-full p-2 rounded border mb-3 bg-white text-sm">
                <option value="category_name">{t.nameEn}</option>
                <option value="category_name_el">{t.nameEl}</option>
              </select>
              <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)}
                className="w-full p-2 rounded border bg-white text-sm">
                <option value="asc">{getOrderLabel("asc")}</option>
                <option value="desc">{getOrderLabel("desc")}</option>
              </select>
            </div>
          )}

          {/* Desktop table + Mobile cards container */}
          <div className="w-full max-w-3xl bg-white/40 rounded-2xl shadow-lg p-3 sm:p-6 backdrop-blur-sm">

            <div className="hidden xl:block overflow-x-auto">
              <table className="w-full text-sm text-center border-collapse text-custom-letters">
                <thead>
                  <tr className="border-b font-semibold">
                    <th className="px-4 py-3">{t.nameEn}</th>
                    <th className="px-4 py-3">{t.nameEl}</th>
                    <th className="px-4 py-3">{t.actions}</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.length > 0 ? categories.map(cat => (
                    <tr key={cat.category_id} className="border-b">
                      <td className="px-4 py-3 font-medium text-custom-letters">{cat.category_name}</td>
                      <td className="px-4 py-3 text-custom-letters">{cat.category_name_el}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-center gap-2">
                          <button onClick={() => handleEditOpen(cat)} title={t.edit}
                            className="bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white p-3 rounded-full">
                            <Pencil size={18} />
                          </button>
                          <button onClick={() => { setCategoryToDelete(cat.category_id); setDeletePopup(true); }} title={t.delete}
                            className="bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white p-3 rounded-full">
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="3" className="py-6 italic text-custom-letters">{t.noCategories}</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="block xl:hidden">
              {categories.length === 0 && <p className="py-6 italic text-custom-letters text-center">{t.noCategories}</p>}
              {categories.map(cat => {
                const isExpanded = expandedRow === cat.category_id;
                return (
                  <div key={cat.category_id}
                    className="border border-gray-300 rounded-xl mb-3 bg-custom-background/60 shadow-sm overflow-hidden w-full">
                    <div className="flex items-center p-2.5 gap-2 w-full min-w-0">
                      <button onClick={() => toggleExpand(cat.category_id)}
                        className="p-1 hover:bg-gray-200 rounded-full transition-transform duration-200 flex-shrink-0"
                        style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                        <ChevronRight size={18} className="text-gray-500" />
                      </button>
                      <div className="flex-1 min-w-0 text-left">
                        <span className="block font-semibold text-custom-letters truncate text-sm">
                          {language === 'el' ? cat.category_name_el : cat.category_name}
                        </span>
                      </div>
                      <div className="flex gap-1.5 flex-shrink-0 ml-auto">
                        <button onClick={() => handleEditOpen(cat)} title={t.edit}
                          className="bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-white p-2 rounded-full shadow-sm">
                          <Pencil size={13} />
                        </button>
                        <button onClick={() => { setCategoryToDelete(cat.category_id); setDeletePopup(true); }} title={t.delete}
                          className="bg-gradient-to-r from-custom-grad1 to-custom-grad4 text-white p-2 rounded-full shadow-sm">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="px-9 pb-3 text-xs text-custom-letters text-left border-t border-gray-100 pt-2.5 bg-white/30 break-words">
                        <p className="mb-1"><span className="font-semibold">{t.nameEn}:</span> {cat.category_name}</p>
                        <p className="mb-1"><span className="font-semibold">{t.nameEl}:</span> {cat.category_name_el}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

          </div>

          {/* Delete confirmation popup */}
          {deletePopup && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div role="dialog" aria-modal="true" aria-labelledby="admincategories-delete-title" className="bg-custom-header rounded-2xl p-6 w-full max-w-xs shadow-xl text-center">
                <h3 id="admincategories-delete-title" className="text-lg font-semibold text-custom-letters mb-3">{t.delTitle}</h3>
                <p className="text-sm text-custom-letters mb-6">{t.delMsg}</p>
                <div className="flex justify-center gap-4">
                  <button onClick={() => setDeletePopup(false)}
                    className="px-5 py-2 text-sm rounded-full bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white hover:opacity-90 transition">
                    {t.cancel}
                  </button>
                  <button onClick={handleDelete}
                    className="px-5 py-2 text-sm rounded-full bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white hover:opacity-90 transition">
                    {t.delete}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Edit popup */}
          {editPopup && editForm && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div role="dialog" aria-modal="true" aria-labelledby="admincategories-edit-title" className="bg-custom-header rounded-2xl p-6 w-full max-w-md shadow-xl">
                <h3 id="admincategories-edit-title" className="text-lg font-semibold text-custom-letters mb-6 text-center">{t.editTitle}</h3>
                <div className="flex flex-col gap-4">
                  {[["category_name", t.nameEn], ["category_name_el", t.nameEl]].map(([field, label]) => (
                    <div key={field} className="flex flex-col gap-1">
                      <span className="text-sm font-semibold text-custom-letters text-left">{label}</span>
                      <input
                        type="text"
                        value={editForm[field] ?? ""}
                        onChange={(e) => setEditForm(prev => ({ ...prev, [field]: e.target.value }))}
                        className="bg-custom-background border border-gray-300 rounded-full px-4 py-2 text-sm text-custom-letters outline-none focus:ring-2 focus:ring-custom-grad1 w-full"
                      />
                    </div>
                  ))}
                </div>
                <div className="flex justify-center gap-4 mt-8">
                  <button onClick={() => { setEditPopup(false); setEditForm(null); }}
                    className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white hover:opacity-90 transition text-sm">
                    {t.cancel}
                  </button>
                  <button onClick={handleEditSave} disabled={saving}
                    className="px-6 py-2 rounded-full bg-gradient-to-r from-custom-grad1 via-custom-grad2 via-custom-grad3 to-custom-grad4 text-white hover:opacity-90 transition disabled:opacity-50 text-sm">
                    {saving ? "..." : t.save}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Add Category Popup Panel */}
          {showCategoryPopup && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div role="dialog" aria-modal="true" aria-labelledby="admincategories-cat-title" className="bg-custom-header rounded-2xl p-6 w-full max-w-sm shadow-xl text-center">
                <h3 id="admincategories-cat-title" className="text-lg font-semibold text-custom-letters mb-4">
                  {t.addCat}
                </h3>

                <input
                  autoFocus
                  className="w-full bg-custom-background px-4 py-3 rounded-full text-sm border-none focus:ring-2 focus:ring-custom-grad1"
                  placeholder={language === "el" ? "π.χ. Γαλακτοκομικά" : "e.g. Dairy"}
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                />

                <div className="flex justify-center gap-3 mt-6">
                  <button
                    onClick={() => { setShowCategoryPopup(false); setNewCategoryName(""); }}
                    className="px-4 py-2 text-sm text-custom-letters hover:text-red-500 transition"
                  >
                    {t.cancel}
                  </button>
                  <button
                    onClick={handleSaveCategory}
                    disabled={!newCategoryName}
                    className={`px-8 py-2 rounded-full font-bold text-white transition text-sm ${newCategoryName ? "bg-gradient-to-r from-custom-grad1 to-custom-grad4 hover:opacity-90" : "bg-gray-300"
                      }`}
                  >
                    {t.save}
                  </button>
                </div>
              </div>
            </div>
          )}

        </main>
      </div>
    </div>
  );
};

export default AdminCategories;